import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import React, { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui/button";
import { Form } from "~/components/ui/form";
import { Loader } from "~/components/ui/loader";
import { Modal, ModalContent } from "~/components/ui/modal";
import { toast } from "~/components/ui/toast";
import type {
  DetectCouriersQuery,
  OrderQuery,
} from "~/graphql/graphql-new.gen";
import {
  useCreateOrderParcelMutation,
  useDetectCouriersQuery,
  useGroupOrderParcelMutation,
  useOrderQuery,
  useOrdersByTrackingQuery,
  useUsedCouriersQuery,
} from "~/graphql/graphql-new.gen";
import { isScopeAuthorized } from "~/libs/auth";
import { activeSaleState } from "~/stores/sales";

import { EstimatedDeliveryDates } from "./estimated-delivery-dates";
import { ModalAddTrackingAlreadyAssignedDifferentRecipient } from "./modal-add-tracking-already-assigned-different-recipient";
import { ModalAddTrackingAlreadyAssignedSameRecipient } from "./modal-add-tracking-already-assigned-same-recipient";
import {
  TrackingNumberFormFields,
  searchCourierState,
  selectedCourierState,
} from "./tracking-number-form-fields";

type TrackingInfoFormProps = {
  orderId: string;
  parcel?: {
    trackingCourier: {
      name: string;
      logoUrl: string;
      slug: string;
    };
    trackingNumber: string;
    trackingUrl: string;
  };
  shipping?: OrderQuery["order"]["shipping"];
};

export type TrackingInfoFormData = {
  trackingCourierSlug: string;
  trackingNumber: string;
};

export enum DetectCourrierError {
  NO_COURIER_FOUND = "NO_COURIER_FOUND",
  INVALID_COURIER = "INVALID_COURIER",
}

export const getDetectCourierError = (
  courier: string,
  detectCouriersData?: DetectCouriersQuery["detectCouriers"],
) => {
  if (detectCouriersData === undefined) return;

  if (detectCouriersData.length === 0) {
    return DetectCourrierError.NO_COURIER_FOUND;
  }
  if (
    detectCouriersData.length > 0 &&
    !detectCouriersData.map((courier) => courier.slug).includes(courier)
  )
    return DetectCourrierError.INVALID_COURIER;
};

const getTrackingErrorMessage = (errorMessage: string, t: TFunction) => {
  switch (errorMessage) {
    case "an_other_order_already_exist_with_the_tracking_info":
      return t("tracking.order_already_exist_with_the_tracking_info");
    case "order_already_fulfilled":
      return t("tracking.order_already_fulfilled");
    default:
      return t("error");
  }
};

const getTrackingMessageFromError = (e: unknown, t: TFunction) => {
  if (Array.isArray(e)) {
    e.forEach((error: Error) => {
      return getTrackingErrorMessage(error.message, t);
    });
  } else if (e instanceof Error) {
    return getTrackingErrorMessage(e.message, t);
  } else {
    return t("error");
  }

  return "";
};

export const TrackingInfoForm: React.FC<TrackingInfoFormProps> = ({
  orderId,
  shipping,
}: TrackingInfoFormProps) => {
  const { user } = useAuth0();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const activeSale = useAtomValue(activeSaleState);
  const setSearchCourier = useSetAtom(searchCourierState);
  const [selectedCourier, setSelectedCourier] = useAtom(selectedCourierState);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const { data: order } = useOrderQuery(
    {
      orderId: orderId,
    },
    {
      enabled: !!orderId,
      refetchOnWindowFocus: false,
      select: (result) => result.order,
    },
  );

  const { refetch: refetchUsedCouriers } = useUsedCouriersQuery(
    {},
    {
      select: (data) => data.usedCouriers,
      enabled: false,
    },
  );

  const form = useForm<TrackingInfoFormData>({
    mode: "onTouched",
    defaultValues: async () => {
      const usedCouriers = await refetchUsedCouriers({});
      const firstUsedCourier = usedCouriers?.data?.[0];
      if (firstUsedCourier) {
        setSearchCourier(firstUsedCourier?.name ?? "");
        setSelectedCourier(firstUsedCourier);
      }
      return {
        trackingCourierSlug: firstUsedCourier?.slug ?? "",
        trackingNumber: "",
      };
    },
  });

  const watchedTrackingCourierSlug = useWatch({
    name: "trackingCourierSlug",
    control: form.control,
  });
  const watchedTrackingNumber = useWatch({
    name: "trackingNumber",
    control: form.control,
  });

  const {
    data: detectCouriersData,
    isFetching: isDetectCouriersFetching,
    refetch: refetchDetectCouriers,
  } = useDetectCouriersQuery(
    {
      input: {
        orderId: orderId,
        trackingNumber: watchedTrackingNumber ?? "",
      },
    },
    {
      select: (data) => data.detectCouriers,
      enabled: false,
    },
  );
  const {
    data: ordersByTrackingData,
    isFetching: isOrdersByTrackingFetching,
    refetch: refetchOrdersByTracking,
  } = useOrdersByTrackingQuery(
    {
      input: {
        saleId: activeSale?.id ?? "",
        trackingCourierSlug: watchedTrackingCourierSlug ?? "",
        trackingNumber: watchedTrackingNumber ?? "",
      },
    },
    {
      select: (data) => data.ordersByTracking,
      enabled: false,
    },
  );

  const {
    mutateAsync: createOrderParcelMutation,
    isPending: createOrderParcelLoading,
    error: createOrderParcelError,
    reset: resetCreateOrderParcel,
  } = useCreateOrderParcelMutation();
  const {
    mutateAsync: groupOrderParcelMutation,
    isPending: groupOrderParcelLoading,
    reset: resetGroupOrderParcel,
  } = useGroupOrderParcelMutation();

  const onSubmit = async (data: TrackingInfoFormData) => {
    const detectCouriersResult = await refetchDetectCouriers();

    if (detectCouriersResult.data === undefined) return;
    const detectCourierError = getDetectCourierError(
      data.trackingCourierSlug,
      detectCouriersResult.data,
    );
    if (!detectCourierError) {
      await checkOrdersByTracking();
    }
  };

  const checkOrdersByTracking = async () => {
    const ordersByTrackingResult = await refetchOrdersByTracking();

    if (ordersByTrackingResult.data === undefined) return;
    if (ordersByTrackingResult.data.length > 0) {
      setModalOpen(true);
    } else {
      createParcel({
        trackingCourierSlug: form.getValues("trackingCourierSlug"),
        trackingNumber: form.getValues("trackingNumber"),
      });
    }
  };

  const createParcel = async (data: TrackingInfoFormData) => {
    try {
      await createOrderParcelMutation({
        input: {
          orderId: orderId,
          trackingInfo: {
            trackingCourierSlug: data.trackingCourierSlug,
            trackingNumber: data.trackingNumber,
          },
        },
      });

      queryClient.invalidateQueries({
        queryKey: useOrderQuery.getKey({ orderId: orderId }),
      });

      toast.success(t("order.tracking.tracking_added"));
    } catch (error) {
      toast.error(getTrackingMessageFromError(error, t));
    }
  };

  const groupParcels = async () => {
    try {
      await groupOrderParcelMutation({
        input: {
          orderId: orderId,
          trackingInfo: {
            trackingCourierSlug: form.getValues("trackingCourierSlug"),
            trackingNumber: form.getValues("trackingNumber") ?? "",
          },
        },
      });

      setModalOpen(false);

      queryClient.invalidateQueries({
        queryKey: useOrderQuery.getKey({ orderId: orderId }),
      });

      toast.success(t("order.tracking.tracking_added"));
    } catch (error) {
      toast.error(getTrackingMessageFromError(error, t));
    }
  };

  const isFetching =
    isDetectCouriersFetching ||
    isOrdersByTrackingFetching ||
    createOrderParcelLoading;

  const submitButton =
    detectCouriersData !== undefined ? (
      <Button
        type="button"
        disabled={
          !isScopeAuthorized(user, "scope.brand.order.parcel.write") ||
          isFetching ||
          !!createOrderParcelError
        }
        onClick={() => checkOrdersByTracking()}
      >
        {t("order.tracking.confirm_add_tracking_number")}
      </Button>
    ) : (
      <Button
        type="submit"
        disabled={
          !isScopeAuthorized(user, "scope.brand.order.parcel.write") ||
          isFetching ||
          !!createOrderParcelError
        }
      >
        {t("order.tracking.add_tracking_number")}
      </Button>
    );

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col space-y-4"
      >
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            {t("order.announced_delivery")}{" "}
            <EstimatedDeliveryDates
              history={shipping?.deliveryDateRangeHistory}
              estimatedDeliveryDateRange={null}
            />
          </div>
        </div>

        <TrackingNumberFormFields
          appearance={
            getDetectCourierError(
              watchedTrackingCourierSlug,
              detectCouriersData,
            )
              ? "warning"
              : createOrderParcelError
                ? "danger"
                : undefined
          }
          detectedCouriers={detectCouriersData}
          onReset={() => {
            resetCreateOrderParcel();
            resetGroupOrderParcel();
          }}
        />

        {isFetching ? (
          <div className="flex items-center justify-center space-x-2 p-2 text-sm">
            <Loader className="size-4" />
            <span className="text-gray-700">
              {t("order.tracking.check_compatibility")}
            </span>
          </div>
        ) : (
          submitButton
        )}
        <Modal open={modalOpen} onOpenChange={() => setModalOpen(false)}>
          <ModalContent scrollable>
            {ordersByTrackingData?.every(
              (o) =>
                o.customerId === order?.customerId &&
                o.recipient.id === order?.recipient.id,
            ) ? (
              <ModalAddTrackingAlreadyAssignedSameRecipient
                detectedOrders={ordersByTrackingData}
                isPending={groupOrderParcelLoading}
                onConfirm={() => groupParcels()}
                trackingInfo={{
                  orderId: orderId,
                  trackingNumber: watchedTrackingNumber,
                  courier: selectedCourier,
                }}
              />
            ) : (
              <ModalAddTrackingAlreadyAssignedDifferentRecipient
                detectedOrders={ordersByTrackingData ?? []}
              />
            )}
          </ModalContent>
        </Modal>
      </form>
    </Form>
  );
};
